import {
  getDisplayYield,
  toNumberOrZero,
  trimToEmpty,
} from "../portfolioReportPositions";

function rowFundId(row) {
  return row?.fund_id || row?.position?.fund_id || "";
}

function aggregateYield(rows = []) {
  let weightedSum = 0;
  let weight = 0;

  rows.forEach((row) => {
    if (row?.is_pending_settlement) return;
    const marketValue = toNumberOrZero(row?.market_value);
    const displayYield = getDisplayYield(row, "period");
    if (displayYield != null && marketValue > 0) {
      weightedSum += displayYield * marketValue;
      weight += marketValue;
    }
  });

  return weight > 0 ? weightedSum / weight : null;
}

function addSnapshotValues(target, rows = [], prefix) {
  rows.forEach((row) => {
    if (row?.is_pending_settlement) return;
    const fundId = rowFundId(row);
    const result = target.get(fundId);
    if (!result) return;

    result[`${prefix}BookValue`] += toNumberOrZero(row?.book_value);
    result[`${prefix}MarketValue`] += toNumberOrZero(row?.market_value);
  });
}

function buildTotal(rows) {
  return rows.reduce(
    (result, row) => ({
      ...result,
      beginningBookValue: result.beginningBookValue + row.beginningBookValue,
      changeInBookValue: result.changeInBookValue + row.changeInBookValue,
      endingBookValue: result.endingBookValue + row.endingBookValue,
      beginningMarketValue: result.beginningMarketValue + row.beginningMarketValue,
      changeInMarketValue: result.changeInMarketValue + row.changeInMarketValue,
      endingMarketValue: result.endingMarketValue + row.endingMarketValue,
      fullyAccruedInterest: result.fullyAccruedInterest + row.fullyAccruedInterest,
    }),
    {
      fundId: "__total__",
      fundName: "Total",
      beginningBookValue: 0,
      changeInBookValue: 0,
      endingBookValue: 0,
      beginningMarketValue: 0,
      changeInMarketValue: 0,
      endingMarketValue: 0,
      fullyAccruedInterest: 0,
    }
  );
}

export function buildFundOverviewData({
  funds = [],
  rows: currentRows = [],
  reportMode = "period",
  periodAnalysis = null,
} = {}) {
  const isPeriodReport = reportMode === "period";
  if (isPeriodReport && !periodAnalysis) return null;

  const beginningRows = isPeriodReport ? periodAnalysis.beginningRows || [] : [];
  const endingRows = isPeriodReport ? periodAnalysis.endingRows || [] : currentRows;
  const rowsByFundId = new Map();

  funds.forEach((fund) => {
    rowsByFundId.set(fund.id, {
      fundId: fund.id,
      fundName: trimToEmpty(fund.name) || "Unnamed Fund",
      beginningBookValue: 0,
      changeInBookValue: 0,
      endingBookValue: 0,
      beginningMarketValue: 0,
      changeInMarketValue: 0,
      endingMarketValue: 0,
      fullyAccruedInterest: 0,
      yield: null,
    });
  });

  addSnapshotValues(rowsByFundId, beginningRows, "beginning");
  addSnapshotValues(rowsByFundId, endingRows, "ending");

  const endingRowsByFundId = new Map();
  endingRows.forEach((row) => {
    const fundId = rowFundId(row);
    if (!endingRowsByFundId.has(fundId)) endingRowsByFundId.set(fundId, []);
    endingRowsByFundId.get(fundId).push(row);
  });

  endingRows.forEach((row) => {
    if (row?.is_pending_settlement) return;
    const result = rowsByFundId.get(rowFundId(row));
    if (result) result.fullyAccruedInterest += toNumberOrZero(row?.accrued_interest);
  });

  const rows = [...rowsByFundId.values()]
    .map((row) => ({
      ...row,
      changeInBookValue: row.endingBookValue - row.beginningBookValue,
      changeInMarketValue: row.endingMarketValue - row.beginningMarketValue,
      yield: isPeriodReport
        ? aggregateYield(endingRowsByFundId.get(row.fundId) || [])
        : null,
    }))
    .sort((a, b) => a.fundName.localeCompare(b.fundName));

  const totals = buildTotal(rows);
  totals.yield = isPeriodReport ? aggregateYield(endingRows) : null;

  return {
    reportMode: isPeriodReport ? "period" : "single",
    portfolioId: funds[0]?.portfolio_id || "",
    periodStart: periodAnalysis?.startDate || "",
    periodEnd: periodAnalysis?.endDate || "",
    rows,
    totals,
  };
}

export default buildFundOverviewData;
